import { create } from "zustand";

export interface DashboardWidgetMeta {
  widgetId: string;
  title: string;
  type: string;
}

export interface DashboardScreenshotState {
  isActive: boolean;
  activeWidgets: DashboardWidgetMeta[];
  widgetBorderVisible: boolean;
  activate: (widgets: DashboardWidgetMeta[]) => void;
  deactivate: () => void;
  setWidgetBorderVisible: (visible: boolean) => void;
}

export const useDashboardScreenshotStore = create<DashboardScreenshotState>((set) => ({
  isActive: false,
  activeWidgets: [],
  widgetBorderVisible: true,
  activate: (widgets) => {
    set({
      isActive: true,
      activeWidgets: widgets
    });
  },
  deactivate: () => {
    set({
      isActive: false,
      activeWidgets: [],
      widgetBorderVisible: true
    });
  },
  setWidgetBorderVisible: (visible) => set({ widgetBorderVisible: visible })
}));

export const selectIsDashboardScreenshotActive = (state: DashboardScreenshotState) =>
  state.isActive;

/**
 * Returns the IDs of all active dashboard screenshot widgets.
 *
 * ⚠️ This selector calls `.map()` and therefore returns a **new array
 * reference on every invocation**. It is safe to use inside another
 * selector that returns a primitive (e.g. `.includes(id)` → boolean),
 * because Zustand compares the outer selector's result by value.
 *
 * Do **not** pass this selector directly to `useDashboardScreenshotStore`:
 * ```ts
 * // ❌ re-renders on every store update regardless of actual change
 * const ids = useDashboardScreenshotStore(selectActiveWidgetIds);
 * ```
 * Instead, wrap it in a boolean or string comparison as
 * `useIsDashboardScreenshotMode` does.
 */
export const selectActiveWidgetIds = (state: DashboardScreenshotState) =>
  state.activeWidgets.map((w) => w.widgetId);
